/**
 * 交易存证常量定义
 * 用于记录用户在交易过程中的关键操作，作为交易存证
 */

// 操作类型枚举
export const OPERATION_TYPES = {
  // 用户注册相关
  USER_REGISTER: 'user_register',           // 注册账号
  ID_CARD_UPLOAD: 'id_card_upload',         // 上传身份证
  BANK_CARD_UPLOAD: 'bank_card_upload',     // 上传银行卡
  AUTH_APPROVED: 'auth_approved',           // 通过审核

  // 订单相关（自己卖）
  ORDER_CREATE: 'order_create',             // 创建订单
  ORDER_AGREE_CONTRACT: 'order_agree_contract', // 勾选协议
  ORDER_SUBMIT: 'order_submit',             // 提交订单
  ORDER_FACE_VERIFY: 'order_face_verify',   // 人脸识别存证

  // 帮别人卖 - 组织者操作
  ORGANIZER_CREATE_ORDER: 'organizer_create_order',       // 组织者创建订单
  ORGANIZER_FILL_SUPPLIER: 'organizer_fill_supplier',     // 填写实供人信息
  ORGANIZER_GENERATE_QR: 'organizer_generate_qr',         // 生成二维码
  ORGANIZER_SHARE_LINK: 'organizer_share_link',           // 分享链接

  // 帮别人卖 - 实供人操作
  SUPPLIER_SCAN_QR: 'supplier_scan_qr',           // 实供人扫码
  SUPPLIER_VERIFY_PHONE: 'supplier_verify_phone', // 实供人验证手机号
  SUPPLIER_CONFIRM_ORDER: 'supplier_confirm_order', // 实供人确认订单
  SUPPLIER_SELECT_BANK: 'supplier_select_bank',   // 实供人选择银行卡
  SUPPLIER_AGREE_CONTRACT: 'supplier_agree_contract', // 实供人同意合同
  SUPPLIER_FACE_VERIFY: 'supplier_face_verify',   // 实供人人脸识别

  // 预约发货相关
  RESERVATION_CREATE: 'reservation_create', // 创建预约
  RESERVATION_SUBMIT: 'reservation_submit', // 提交预约

  // 结算相关
  SETTLEMENT_CONFIRM: 'settlement_confirm', // 确认结算
  SETTLEMENT_AGREE_CONTRACT: 'settlement_agree_contract', // 勾选结算协议
  SETTLEMENT_FACE_VERIFY: 'settlement_face_verify', // 结算人脸识别

  // 单据查看相关
  DOCUMENT_VIEW: 'document_view',           // 查看单据
};

// 操作类型标签
export const OPERATION_TYPE_LABELS = {
  [OPERATION_TYPES.USER_REGISTER]: '用户注册',
  [OPERATION_TYPES.ID_CARD_UPLOAD]: '上传身份证',
  [OPERATION_TYPES.BANK_CARD_UPLOAD]: '上传银行卡',
  [OPERATION_TYPES.AUTH_APPROVED]: '审核通过',
  [OPERATION_TYPES.ORDER_CREATE]: '创建订单',
  [OPERATION_TYPES.ORDER_AGREE_CONTRACT]: '同意订单协议',
  [OPERATION_TYPES.ORDER_SUBMIT]: '提交订单',
  [OPERATION_TYPES.ORDER_FACE_VERIFY]: '订单人脸识别',
  // 帮别人卖 - 组织者
  [OPERATION_TYPES.ORGANIZER_CREATE_ORDER]: '组织者创建订单',
  [OPERATION_TYPES.ORGANIZER_FILL_SUPPLIER]: '填写实供人信息',
  [OPERATION_TYPES.ORGANIZER_GENERATE_QR]: '生成确认二维码',
  [OPERATION_TYPES.ORGANIZER_SHARE_LINK]: '分享确认链接',
  // 帮别人卖 - 实供人
  [OPERATION_TYPES.SUPPLIER_SCAN_QR]: '实供人扫码',
  [OPERATION_TYPES.SUPPLIER_VERIFY_PHONE]: '实供人验证身份',
  [OPERATION_TYPES.SUPPLIER_CONFIRM_ORDER]: '实供人确认订单',
  [OPERATION_TYPES.SUPPLIER_SELECT_BANK]: '实供人选择银行卡',
  [OPERATION_TYPES.SUPPLIER_AGREE_CONTRACT]: '实供人同意合同',
  [OPERATION_TYPES.SUPPLIER_FACE_VERIFY]: '实供人人脸识别',
  // 其他
  [OPERATION_TYPES.RESERVATION_CREATE]: '创建预约',
  [OPERATION_TYPES.RESERVATION_SUBMIT]: '提交预约',
  [OPERATION_TYPES.SETTLEMENT_CONFIRM]: '确认结算',
  [OPERATION_TYPES.SETTLEMENT_AGREE_CONTRACT]: '同意结算协议',
  [OPERATION_TYPES.SETTLEMENT_FACE_VERIFY]: '结算人脸识别',
  [OPERATION_TYPES.DOCUMENT_VIEW]: '查看单据',
};

// 操作类型分组
export const OPERATION_GROUPS = {
  registration: {
    label: '用户注册',
    types: [
      OPERATION_TYPES.USER_REGISTER,
      OPERATION_TYPES.ID_CARD_UPLOAD,
      OPERATION_TYPES.BANK_CARD_UPLOAD,
      OPERATION_TYPES.AUTH_APPROVED,
    ],
  },
  order: {
    label: '发起订单',
    types: [
      OPERATION_TYPES.ORDER_CREATE,
      OPERATION_TYPES.ORDER_AGREE_CONTRACT,
      OPERATION_TYPES.ORDER_SUBMIT,
      OPERATION_TYPES.ORDER_FACE_VERIFY,
    ],
  },
  organizer: {
    label: '组织者代客下单',
    types: [
      OPERATION_TYPES.ORGANIZER_CREATE_ORDER,
      OPERATION_TYPES.ORGANIZER_FILL_SUPPLIER,
      OPERATION_TYPES.ORGANIZER_GENERATE_QR,
      OPERATION_TYPES.ORGANIZER_SHARE_LINK,
    ],
  },
  supplier: {
    label: '实供人确认',
    types: [
      OPERATION_TYPES.SUPPLIER_SCAN_QR,
      OPERATION_TYPES.SUPPLIER_VERIFY_PHONE,
      OPERATION_TYPES.SUPPLIER_CONFIRM_ORDER,
      OPERATION_TYPES.SUPPLIER_SELECT_BANK,
      OPERATION_TYPES.SUPPLIER_AGREE_CONTRACT,
      OPERATION_TYPES.SUPPLIER_FACE_VERIFY,
    ],
  },
  reservation: {
    label: '预约发货',
    types: [
      OPERATION_TYPES.RESERVATION_CREATE,
      OPERATION_TYPES.RESERVATION_SUBMIT,
    ],
  },
  settlement: {
    label: '确认结算',
    types: [
      OPERATION_TYPES.SETTLEMENT_CONFIRM,
      OPERATION_TYPES.SETTLEMENT_AGREE_CONTRACT,
      OPERATION_TYPES.SETTLEMENT_FACE_VERIFY,
    ],
  },
  document: {
    label: '查看单据',
    types: [
      OPERATION_TYPES.DOCUMENT_VIEW,
    ],
  },
};

// 存证类型枚举
export const EVIDENCE_TYPES = {
  NONE: 'none',             // 无存证
  IMAGE: 'image',           // 图片存证
  VIDEO: 'video',           // 视频存证
  FACE_IMAGE: 'face_image', // 人脸图片
  FACE_VIDEO: 'face_video', // 人脸视频
  ID_CARD_FRONT: 'id_card_front', // 身份证正面
  ID_CARD_BACK: 'id_card_back',   // 身份证反面
  BANK_CARD: 'bank_card',   // 银行卡照片
  CONTRACT: 'contract',     // 协议截图
  QR_CODE: 'qr_code',       // 二维码截图
  SCREENSHOT: 'screenshot', // 屏幕截图
};

// 存证类型标签
export const EVIDENCE_TYPE_LABELS = {
  [EVIDENCE_TYPES.NONE]: '无存证',
  [EVIDENCE_TYPES.IMAGE]: '图片存证',
  [EVIDENCE_TYPES.VIDEO]: '视频存证',
  [EVIDENCE_TYPES.FACE_IMAGE]: '人脸图片',
  [EVIDENCE_TYPES.FACE_VIDEO]: '人脸视频',
  [EVIDENCE_TYPES.ID_CARD_FRONT]: '身份证正面',
  [EVIDENCE_TYPES.ID_CARD_BACK]: '身份证反面',
  [EVIDENCE_TYPES.BANK_CARD]: '银行卡照片',
  [EVIDENCE_TYPES.CONTRACT]: '协议截图',
  [EVIDENCE_TYPES.QR_CODE]: '二维码截图',
  [EVIDENCE_TYPES.SCREENSHOT]: '屏幕截图',
};

// 单据类型枚举
export const DOCUMENT_TYPES = {
  ORDER: 'order',           // 订单
  DELIVERY: 'delivery',     // 送货单
  STOCKIN: 'stockin',       // 入库单
  SETTLEMENT: 'settlement', // 结算单
  INVOICE: 'invoice',       // 发票单
  PAYMENT: 'payment',       // 付款单
};

// 单据类型标签
export const DOCUMENT_TYPE_LABELS = {
  [DOCUMENT_TYPES.ORDER]: '订单',
  [DOCUMENT_TYPES.DELIVERY]: '送货单',
  [DOCUMENT_TYPES.STOCKIN]: '入库单',
  [DOCUMENT_TYPES.SETTLEMENT]: '结算单',
  [DOCUMENT_TYPES.INVOICE]: '发票单',
  [DOCUMENT_TYPES.PAYMENT]: '付款单',
};

// 操作日志数据结构
// {
//   id: string,              // 日志ID
//   userId: string,          // 用户ID
//   userName: string,        // 用户姓名
//   userPhone: string,       // 用户手机号
//   operationType: string,   // 操作类型 (OPERATION_TYPES)
//   operationGroup: string,  // 操作分组
//   timestamp: string,       // 操作时间戳 (ISO格式)
//   description: string,     // 操作描述
//
//   // 关联单据信息
//   documentType: string,    // 单据类型 (DOCUMENT_TYPES)
//   documentId: string,      // 单据ID
//   documentNo: string,      // 单据编号
//
//   // 存证信息
//   evidenceType: string,    // 存证类型 (EVIDENCE_TYPES)
//   evidenceUrl: string,     // 存证文件URL
//   evidenceThumbnail: string, // 存证缩略图URL
//
//   // 帮别人卖模式额外信息
//   isOrganizerMode: boolean,    // 是否为帮别人卖模式
//   organizerId: string,         // 组织者ID
//   organizerName: string,       // 组织者姓名
//   supplierId: string,          // 实供人ID
//   supplierName: string,        // 实供人姓名
//   supplierPhone: string,       // 实供人手机号
//
//   // 业务数据快照
//   snapshot: object,        // 操作时的业务数据快照
//
//   // 设备信息
//   deviceInfo: {
//     ip: string,            // IP地址
//     device: string,        // 设备类型
//     os: string,            // 操作系统
//     browser: string,       // 浏览器
//     location: string,      // 地理位置
//   },
//
//   // 备注信息
//   remark: string,
// }
